import React, { useState, useEffect, createContext, useContext, useMemo, useCallback, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import UserManagement from './pages/UserManagement';
import Customers from './pages/Customers';
import CustomerDashboard from './pages/CustomerDashboard';
import Transactions from './pages/Transactions';
import Repairs from './pages/Repairs';
import CustomJobs from './pages/CustomJobs';
import Appraisals from './pages/Appraisals';
import Messages from './pages/Messages';
import Shops from './pages/Shops';
import TaxReports from './pages/TaxReports';
import Performance from './pages/Performance';
import Shoutout from './pages/Shoutout';
import IPWhitelist from './pages/IPWhitelist';
import Layout from './components/Layout';

const AuthContext = createContext(null);
const UnreadCountContext = createContext(null);
const SocketContext = createContext(null);
const ShopContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useUnreadCountContext = () => useContext(UnreadCountContext);
export const useSocket = () => useContext(SocketContext);
export const useShop = () => useContext(ShopContext);

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Protected route wrapper component - defined outside App to prevent re-creation on every render
const ProtectedRoute = ({ children, user, needsOnboarding, adminOnly = false }) => {
  if (!user) {
    // Redirect to onboarding if user hasn't onboarded yet, otherwise to login
    return <Navigate to={needsOnboarding ? "/onboarding" : "/login"} replace />;
  }
  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  return children;
};

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(null); // null = not yet checked
  const [unreadCount, setUnreadCount] = useState(0);
  const [socketKey, setSocketKey] = useState(0); // Force context update when socket changes
  const [kioskModeLogout, setKioskModeLogout] = useState(false);
  const [isKioskSession, setIsKioskSession] = useState(() => localStorage.getItem('kioskSession') === 'true');
  const [showKioskReauth, setShowKioskReauth] = useState(false);
  const [kioskDeadline, setKioskDeadline] = useState(() => {
    const dl = localStorage.getItem('kioskDeadline');
    return dl ? parseInt(dl, 10) : null;
  });
  const inactivityTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const socketRef = useRef(null); // Persist socket across navigations
  const KIOSK_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

  // Shop state
  const [shops, setShops] = useState([]);
  const [currentShop, setCurrentShop] = useState(null);
  const [shopsLoading, setShopsLoading] = useState(true);

  // Web Audio API notification chime - two-tone pleasant sound
  // Initialized on first user gesture (login) for iOS Safari compatibility
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  // Request browser notification permission (only prompts once per browser/domain)
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        return true;
      } else if (Notification.permission === 'denied') {
        console.warn('Browser notifications are blocked - user must enable in site settings');
        return false;
      }
      // Permission is 'default' - first time, show browser prompt
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }, []);

  // Show a native browser notification (appears even when browser is minimized)
  const showBrowserNotification = useCallback((title, body) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: 'new-sms', // Replaces previous notification instead of stacking
        requireInteraction: false,
      });
      // Open the Messages page when user clicks the notification
      notification.onclick = () => {
        window.focus();
        window.location.href = '/messages';
      };
    }
  }, []);

  const playNotificationSound = useCallback(() => {
    try {
      const ctx = getAudioContext();
      const now = ctx.currentTime;

      // First tone - higher pitch
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(880, now); // A5
      gain1.gain.setValueAtTime(0.3, now);
      gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
      osc1.connect(gain1).connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.15);

      // Second tone - slightly lower, plays after a short delay
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(1100, now + 0.12); // C#6
      gain2.gain.setValueAtTime(0.3, now + 0.12);
      gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
      osc2.connect(gain2).connect(ctx.destination);
      osc2.start(now + 0.12);
      osc2.stop(now + 0.35);
    } catch (error) {
      console.error('Failed to play notification sound:', error);
    }
  }, [getAudioContext]);

  // Resume AudioContext on first user interaction for iOS Safari
  const resumeAudioContext = useCallback(() => {
    try {
      getAudioContext();
    } catch (error) {
      // Web Audio API not supported, ignore silently
    }
  }, [getAudioContext]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await fetch('/api/conversations/unread-count', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
        return data.count;
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
    return null;
  }, []);

  const unreadCountValue = useMemo(() => ({ unreadCount, fetchUnreadCount }), [unreadCount, fetchUnreadCount]);

  // Fetch all shops and set current shop from localStorage or first shop
  const fetchShops = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/shops`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setShops(data.shops);
        // Check localStorage for saved shop_id
        const savedShopId = localStorage.getItem('activeShopId');
        if (savedShopId && data.shops.length > 0) {
          const matched = data.shops.find(s => s.shop_id === savedShopId);
          if (matched) {
            setCurrentShop(matched);
          } else {
            // Saved shop_id no longer exists, fall back to first
            setCurrentShop(data.shops[0] || null);
            localStorage.setItem('activeShopId', (data.shops[0] || {}).shop_id || '');
          }
        } else if (data.shops.length > 0) {
          // No saved shop_id, default to first
          setCurrentShop(data.shops[0]);
          localStorage.setItem('activeShopId', data.shops[0].shop_id);
        } else {
          // No shops at all: drop any stale id so page filters don't use a ghost shop
          setCurrentShop(null);
          localStorage.removeItem('activeShopId');
        }
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
    } finally {
      setShopsLoading(false);
    }
  }, []);

  // Select a shop by its id (numeric PK) - persists shop_id to localStorage
  const selectShop = useCallback((shopId) => {
    const shop = shops.find(s => s.id === shopId);
    if (shop) {
      setCurrentShop(shop);
      localStorage.setItem('activeShopId', shop.shop_id);
    }
  }, [shops]);

  const shopValue = useMemo(() => ({
    shops, currentShop, selectShop, shopsLoading, refreshShops: fetchShops
  }), [shops, currentShop, selectShop, shopsLoading, fetchShops]);

  useEffect(() => {
    checkOnboardingStatus();
    fetchUser();
    fetchUnreadCount();
  }, []);

  // Fetch shops only when a session exists: after the initial auth check, login,
  // kiosk loginAs, or register. Calling it unauthenticated returned a 401 that
  // silently skipped persisting activeShopId (the intermittent missing-shop bug).
  useEffect(() => {
    if (user) {
      fetchShops();
    } else if (!loading) {
      // No session: clear in-memory shop state and unblock the loading screen
      setShops([]);
      setCurrentShop(null);
      setShopsLoading(false);
    }
  }, [user, loading, fetchShops]);

  const checkOnboardingStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/auth/onboarding-status`, {
        credentials: 'include'
      });
      const data = await res.json();
      console.log({checkOnboardingStatus:data})
      setNeedsOnboarding(data.needsOnboarding);
    } catch (error) {
      console.error('Failed to check onboarding status:', error);
      setNeedsOnboarding(false); // Default to false on error
    }
  };

  const fetchUser = async () => {
    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });

    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || 'Login failed');
    }

    const data = await res.json();
    setUser(data.user);
    setIsKioskSession(false);
    // Resume AudioContext on login gesture for iOS Safari compatibility
    resumeAudioContext();
    // Request browser notification permission on login (only prompts once per browser/domain)
    requestNotificationPermission();
    return data.user;
  };

  const clearKioskState = () => {
    localStorage.removeItem('kioskSession');
    localStorage.removeItem('kioskDeadline');
  };

  const logout = async () => {
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    setKioskModeLogout(false);
    setIsKioskSession(false);
    setKioskDeadline(null);
    clearKioskState();
    setUser(null);
  };

  const kioskLogout = async () => {
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    setKioskModeLogout(true);
    setIsKioskSession(false);
    setKioskDeadline(null);
    clearKioskState();
    setUser(null);
  };

  const clearAuthToken = async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Failed to clear auth token:', error);
    }
  };

  const loginAs = (newUser) => {
    const deadline = Date.now() + KIOSK_TIMEOUT_MS;
    setUser(newUser);
    setKioskModeLogout(false);
    setIsKioskSession(true);
    setKioskDeadline(deadline);
    localStorage.setItem('kioskSession', 'true');
    localStorage.setItem('kioskDeadline', String(deadline));
  };

  const register = async (name, email, password) => {
    const res = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, email, password })
    });
    
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || 'Registration failed');
    }
    
    const data = await res.json();
    setUser(data.user);
    setNeedsOnboarding(false);
    return data.user;
  };

  // Memoize auth context value to prevent unnecessary re-renders
  const authValue = useMemo(() => ({
    user, login, logout, kioskLogout, loginAs, register, needsOnboarding, kioskModeLogout, isKioskSession, showKioskReauth, setShowKioskReauth, kioskDeadline, clearAuthToken
  }), [
    user, login, logout, kioskLogout, loginAs, register, needsOnboarding, kioskModeLogout, isKioskSession, showKioskReauth, setShowKioskReauth, kioskDeadline, clearAuthToken
  ]);

  // Kiosk inactivity timeout - show reauth PIN pad after 10 min of no activity
  useEffect(() => {
    const events = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];

    const resetTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      const deadline = Date.now() + KIOSK_TIMEOUT_MS;
      setKioskDeadline(deadline);
      inactivityTimerRef.current = setTimeout(() => {
        console.log('Kiosk session expired due to inactivity');
        (async () => {
          await fetch(`${API_URL}/auth/logout`, {
            method: 'POST',
            credentials: 'include'
          });
          setShowKioskReauth(true);
        })();
      }, KIOSK_TIMEOUT_MS);
    };

    if (!isKioskSession || !user) {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }

    // Start initial timer
    resetTimer();

    // Listen for activity events
    events.forEach(evt => {
      document.addEventListener(evt, resetTimer, { capture: true, passive: true });
    });

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      events.forEach(evt => {
        document.removeEventListener(evt, resetTimer, { capture: true });
      });
    };
  }, [isKioskSession, user]);

  // Global Socket.io connection for real-time message notifications
  // Socket is stored in a ref to persist across navigations without disconnecting.
  // NO cleanup disconnect - socket only disconnects when user logs out.
  useEffect(() => {
    if (!user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    // Reuse existing socket if already connected (prevents disconnect on navigation)
    if (socketRef.current && socketRef.current.connected) {
      console.log('Reusing existing socket connection');
      return;
    }

    const newSocket = io(window.location.origin, {
      path: '/api/socket.io',
      withCredentials: true,
      transports: ['websocket', 'polling'] // Prefer WebSocket for instant delivery, fallback to polling
    });

    newSocket.on('connect', () => {
      console.log('Global socket.io connected');
    });

    newSocket.on('disconnect', () => {
      console.log('Global socket.io disconnected');
    });

    newSocket.on('new-message', async (data) => {
      console.log('Global: New message received via Socket.io:', data);
      // Update unread count badge
      const newCount = await fetchUnreadCount();
      // Play notification sound only when NOT on the Messages page
      if (window.location.pathname !== '/messages') {
        playNotificationSound();
        // Show browser notification when not on Messages page
        const sender = data.customerPhone || 'Unknown';
        const messagePreview = data.body ? (data.body.length > 80 ? data.body.slice(0, 80) + '...' : data.body) : 'New message';
        showBrowserNotification(`New message from ${sender}`, messagePreview);
      }
    });

    socketRef.current = newSocket;
    // Notify context consumers that socket is ready (without storing socket in state)
    setSocketKey(prev => prev + 1);

    // Intentionally NO cleanup disconnect here
    // Socket persists across navigations and only disconnects on logout
  }, [user]);

  // Tab visibility change handler - refetch unread count when user returns to the tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Tab became visible, refetching unread count...');
        fetchUnreadCount();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUnreadCount]);

  // Update document title with unread count badge
  useEffect(() => {
    if (unreadCount > 0) {
      document.title = `(${unreadCount}) EJR POS`;
    } else {
      document.title = 'EJR POS';
    }
    // Clear title when navigating to Messages page (messages are marked as read there)
    return () => {
      // Cleanup not needed - title updates on next state change
    };
  }, [unreadCount]);

  // Stable socket value for context - always reads from ref
  const socketValue = useMemo(() => socketRef.current, [socketKey]);

  // Show loading screen while checking onboarding status or fetching user
  if (loading || needsOnboarding === null || shopsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  // console.log('App render - user:', user, 'needsOnboarding:', needsOnboarding);
  return (
    <AuthContext.Provider value={authValue}>
      <UnreadCountContext.Provider value={unreadCountValue}>
        <SocketContext.Provider value={socketValue}>
          <ShopContext.Provider value={shopValue}>
      <Routes>
        <Route path="/onboarding" element={
          needsOnboarding && !user ? <Onboarding /> : <Navigate to="/" replace />
        } />
        <Route path="/login" element={
          !user ? <Login /> : <Navigate to="/" replace />
        } />
        <Route path="/" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding}>
            <Layout><Dashboard /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding}>
            <Layout><Profile /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding} adminOnly>
            <Layout><UserManagement /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/customers" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding}>
            <Layout><Customers /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/customers/:customerId/dashboard" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding}>
            <Layout><CustomerDashboard /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/transactions" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding}>
            <Layout><Transactions /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/repairs" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding}>
            <Layout><Repairs /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/custom-jobs" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding}>
            <Layout><CustomJobs /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/appraisals" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding}>
            <Layout><Appraisals /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/messages" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding}>
            <Layout><Messages /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/shops" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding} adminOnly>
            <Layout><Shops /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/performance" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding} adminOnly>
            <Layout><Performance /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/tax-reports" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding} adminOnly>
            <Layout><TaxReports /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/shoutout" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding} adminOnly>
            <Layout><Shoutout /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/ip-whitelist" element={
          <ProtectedRoute user={user} needsOnboarding={needsOnboarding} adminOnly>
            <Layout><IPWhitelist /></Layout>
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
          </ShopContext.Provider>
        </SocketContext.Provider>
      </UnreadCountContext.Provider>
    </AuthContext.Provider>
  );
}
